import React, { useState } from 'react';
import {
    AlignCenterOutlinedIcon,
    Button,
    Col,
    Divider,
    EditIcon,
    FontColorsOutlinedIcon,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Text,
    Tooltip,
    VisibilityIcon,
} from '@servahealth/serva-ui-kit';
import { ReactComponent as HtmlIcon } from '../../Images/code-slash.svg';
import StopOutlinedIcon from '../Icons/StopOutlinedIcon';
import DataSetColumnFormatModel from '../../Interfaces/Responses/Reports/DataSets/DatasetColumnFormatModel';
import { CoreDataPoint, CoreType } from '../Shared/CoreComponents';
import AHRBCoreDataPointCustomizationsFormat from './AHRBCoreDataPointCustomizationsFormat';
import { StringUtil } from '../../Utils/StringUtil';

export interface AHRBCoreDataPointCustomizationProps {
    coreTypes: CoreType[];
    coreDataPoints: CoreDataPoint[];
    ahrbsDataPointID: number | undefined;
    selectedCoreDataPointColumnCustomization: DataSetColumnFormatModel;
    onOk: (
        dataPointColumnFormat: DataSetColumnFormatModel
    ) => void;
    onCancel: () => void;
}

export const AHRBCoreDataPointCustomization: React.FC<AHRBCoreDataPointCustomizationProps> = ({ ...props }) => {

    const [selectedCoreDataPointColumnFormat, setSelectedCoreDataPointColumnFormat] = useState<DataSetColumnFormatModel>({ ...props.selectedCoreDataPointColumnCustomization });
    const [changeOccurred, setChangeOccurred] = useState(false);
    const [dataSetColumnTitleEditOpen, setDataSetColumnTitleEditOpen] = useState(false);
    const [dataSetColumnFormatEditOpen, setDataSetColumnFormatEditOpen] = useState(false);
    
    const selectedFieldName = props.coreDataPoints.find(cdp => cdp.id === props.ahrbsDataPointID)?.name;
    const selectedFieldTypeID = props.coreDataPoints.find(cdp => cdp.id === props.ahrbsDataPointID)?.idType;
    const selectedTypeName = props.coreTypes.find(ct => ct.id === selectedFieldTypeID)?.name;

    const canFormat = selectedCoreDataPointColumnFormat.jsDataType === 'number' || selectedCoreDataPointColumnFormat.jsDataType === 'date';

    const handleChange = (e: string, typeOfChange: string) => {
        setChangeOccurred(true);

        setSelectedCoreDataPointColumnFormat(function (currentColumnFormat) {
            const updatedColumnFormat = { ...currentColumnFormat };

            if (typeOfChange === 'alignment') {
                updatedColumnFormat.alignment = e;
            }
            else if (typeOfChange === 'allowHtml') {
                updatedColumnFormat.allowHtml = e === 'HTML';
            }
            else if (typeOfChange === 'columnTitle') {
                updatedColumnFormat.columnTitle = e;
            }
            else if (typeOfChange === 'format') {
                updatedColumnFormat.format = e;
            }
            else if (typeOfChange === 'visibility') {
                updatedColumnFormat.hide = e === 'Hidden';
            }
            else if (typeOfChange === 'jsDataType') {
                if (updatedColumnFormat.jsDataType !== e) {
                    updatedColumnFormat.format = '';
                }

                updatedColumnFormat.jsDataType = e;
            }

            return updatedColumnFormat;
        });
    };

    const handleAlignmentChange = (e: string) => {
        handleChange(e, 'alignment');
    };

    const handleAllowHtmlChange = (e: string) => {
        handleChange(e, 'allowHtml');
    };

    const handleColumnTitleChange = (columnTitle: string | undefined) => {
        handleChange(columnTitle ?? '', 'columnTitle');
    };

    const handleColumnFormatChange = (format: string | undefined) => {
        handleChange(format ?? '', 'format');
    };

    const handleVisibilityChange = (e: string) => {
        handleChange(e, 'visibility');
    };

    const handleJSDataTypeChange = (e: string) => {
        handleChange(e, 'jsDataType');
    }

    const handleColumnFormatOrTitleChange = (typeOfChange: string) => {

        if (typeOfChange === 'format') {
            if (canFormat) {
                setDataSetColumnFormatEditOpen(true);
            }
        }
        else {
            setDataSetColumnTitleEditOpen(true);
        }
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10',
        border: `1px solid black`,
        marginTop: 4,
        height: '95%'
    };

    return (
        <>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={dataSetColumnTitleEditOpen}
                width={'700px'}
                onCancel={() => setDataSetColumnTitleEditOpen(false)}
            >
                <AHRBCoreDataPointCustomizationsFormat
                    dataSetColumnFormat={undefined}
                    dataSetColumnTitle={selectedCoreDataPointColumnFormat}
                    updateDataSetColumnFormat={(format: string | undefined) => {}}
                    updateDataSetColumnTitle={handleColumnTitleChange}
                    close={() => setDataSetColumnTitleEditOpen(false)}
                ></AHRBCoreDataPointCustomizationsFormat>
            </ServaModal>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={dataSetColumnFormatEditOpen}
                width={'700px'}
                onCancel={() => setDataSetColumnFormatEditOpen(false)}
            >
                <AHRBCoreDataPointCustomizationsFormat
                    dataSetColumnFormat={selectedCoreDataPointColumnFormat}
                    dataSetColumnTitle={undefined}
                    updateDataSetColumnFormat={handleColumnFormatChange}
                    updateDataSetColumnTitle={(title: string | undefined) => {}}
                    close={() => setDataSetColumnFormatEditOpen(false)}
                ></AHRBCoreDataPointCustomizationsFormat>
            </ServaModal>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={4} />
                <Col span={16}>
                    <div style={contentStyle}>
                        <h3 className='text-2xl pt-2 px-2'>Formatting</h3>
                    </div>
                </Col>
                <Col span={4} />
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={1} />
                <Col span={22}>
                    <div>
                        <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                    </div>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16} className='retainbi-compact-row' style={{ backgroundColor: 'palegreen', borderRadius: '8px' }}>
                <Col span={1} />
                <Col span={11}>
                    <Text italic strong>Field:</Text>{" "}
                    <Text>{selectedFieldName}</Text>
                </Col>
                <Col span={11}>
                    <Text italic strong>Type:</Text>{" "}
                    <Text>{selectedTypeName}</Text>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={24}>
                    <div>
                        <Divider style={{ margin: '1px' }}></Divider>
                    </div>
                </Col>
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={5}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'left'}}>
                        <Text italic type='secondary'>COLUMN TITLE</Text>
                    </div>
                </Col>
                <Col span={3}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                        <AlignCenterOutlinedIcon tooltipText='alignment'></AlignCenterOutlinedIcon>
                    </div>
                </Col>
                <Col span={4}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                        <FontColorsOutlinedIcon tooltipText='format'></FontColorsOutlinedIcon>
                    </div>
                </Col>
                <Col span={4}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                        <VisibilityIcon tooltipText='visibility'></VisibilityIcon>
                    </div>
                </Col>
                <Col span={4}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                        <Tooltip title="allow html">
                            <HtmlIcon></HtmlIcon>
                        </Tooltip>
                    </div>
                </Col>
                <Col span={4}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign: 'center'}}>
                        <Text italic type='secondary'>Data Type</Text>
                    </div>
                </Col>
            </Row>
            <Divider style={{ border: '1px solid lightgray', margin: '4px' }} />
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={4}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'left'}}>
                        <label>{selectedFieldName}</label>
                    </div>
                </Col>
                <Col span={1}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                        <EditIcon
                            onClick={() => handleColumnFormatOrTitleChange('columnTitle')}
                        ></EditIcon>
                    </div>
                </Col>
                <Col span={3}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                        <Select
                            key={'AL-00'}
                            style={{ width: '80%' }}
                            value={StringUtil.CapitalizeFirstLetter(selectedCoreDataPointColumnFormat.alignment)}
                            onChange={(e: any) => handleAlignmentChange(e)}
                        >
                            <Option value="">(None)</Option>
                            <Option value="Left">Left</Option>
                            <Option value="Center">Center</Option>
                            <Option value="Right">Right</Option>
                        </Select>
                    </div>
                </Col>
                {
                    <>
                        {
                            (canFormat)
                            ?
                            (
                                <Col span={3}>
                                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'left'}}>
                                        <label>{selectedCoreDataPointColumnFormat.format}</label>
                                    </div>
                                </Col>
                            )
                            :
                            (
                                <Col span={3}>
                                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                                        <Text italic type='secondary'>N / A</Text>
                                    </div>
                                </Col>
                            )
                        }
                    </>
                }
                {
                    <>
                        {
                            (canFormat)
                            ?
                            (
                                <Col span={1}>
                                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                                        <EditIcon
                                            onClick={() => handleColumnFormatOrTitleChange('format')}
                                        ></EditIcon>
                                    </div>
                                </Col>
                            )
                            :
                            (
                                <Col span={1}>
                                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                                        <StopOutlinedIcon></StopOutlinedIcon>
                                    </div>
                                </Col>
                            )
                        }
                    </>
                }
                <Col span={4}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                        <Select
                            key={'VI-00'}
                            style={{ width: '80%' }}
                            value={selectedCoreDataPointColumnFormat.hide ? 'Hidden' : 'Visible'}
                            onChange={(e: any) => handleVisibilityChange(e)}
                        >
                            <Option value="Visible">Visible</Option>
                            <Option value="Hidden">Hidden</Option>
                        </Select>
                    </div>
                </Col>
                <Col span={4}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign:'center'}}>
                        <Select
                            key={'HT-00'}
                            style={{ width: '80%' }}
                            value={selectedCoreDataPointColumnFormat.allowHtml ? 'HTML' : 'Plain'}
                            onChange={(e: any) => handleAllowHtmlChange(e)}
                        >
                            <Option value="HTML">HTML</Option>
                            <Option value="Plain">Plain</Option>
                        </Select>
                    </div>
                </Col>
                <Col span={4}>
                    <div style={{paddingTop: '0px', paddingBottom: '0px', textAlign: 'center'}}>
                        <Select
                            key={'JS-00'}
                            style={{ width: '80%' }}
                            value={selectedCoreDataPointColumnFormat.jsDataType}
                            onChange={(e: any) => handleJSDataTypeChange(e)}
                        >
                            <Option value="date">Date</Option>
                            <Option value="number">Number</Option>
                            <Option value="string">String</Option>
                        </Select>
                    </div>
                </Col>
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={24}>
                    <div>
                        <Divider style={{ margin: '1px' }}></Divider>
                    </div>
                </Col>
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={15} />
                <Col span={4}>
                    <div style={{ textAlign: 'right' }}>
                            <Button
                                disabled={!changeOccurred}
                                htmlType="button"
                                style={{width: '95%'}}
                                type='primary'
                                onClick={() => {props.onOk(selectedCoreDataPointColumnFormat);}}
                            >
                                OK
                            </Button>
                    </div>
                </Col>
                <Col span={4}>
                    <div style={{ textAlign: 'right' }}>
                            <Button
                                style={{width: '95%'}}
                                type="default"
                                onClick={() => props.onCancel()}
                            >
                                Cancel
                            </Button>
                    </div>
                </Col>
                <Col span={1} />
            </Row>
        </>
    );
};

export default AHRBCoreDataPointCustomization;